import React from 'react';
import { AlertCircle, AlertTriangle, Link2 } from 'lucide-react';
import { ValidationError, PCCase } from '../../types';

interface ValidationErrorsProps {
  errors: ValidationError[];
  warnings?: string[];
  duplicateCase?: PCCase;
  onViewDuplicate?: (caseId: string) => void;
}

export const ValidationErrors: React.FC<ValidationErrorsProps> = ({
  errors,
  warnings = [],
  duplicateCase,
  onViewDuplicate,
}) => {
  if (errors.length === 0 && warnings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Errors */}
      {errors.length > 0 && (
        <div className="p-4 bg-danger-soft border border-danger/40 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={18} className="text-danger" />
            <h4 className="text-sm font-medium text-danger">
              Please fix the following errors:
            </h4>
          </div>
          <ul className="space-y-1 ml-6">
            {errors.map((error, index) => (
              <li
                key={`${error.field}-${index}`}
                className="text-sm text-danger list-disc"
              >
                <span className="font-medium">{formatFieldName(error.field)}:</span>{' '}
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Duplicate Warning */}
      {duplicateCase && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-xl">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="text-amber-500 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Potential Duplicate Detected
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                A case with the name "{duplicateCase.name}" by{' '}
                {duplicateCase.manufacturer} already exists.
              </p>
              {onViewDuplicate && (
                <button
                  type="button"
                  onClick={() => onViewDuplicate(duplicateCase.id)}
                  className="inline-flex items-center gap-1 mt-2 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 font-medium"
                >
                  <Link2 size={14} />
                  View existing case
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">Warnings:</h4>
          </div>
          <ul className="space-y-1 ml-6">
            {warnings.map((warning, index) => (
              <li key={index} className="text-sm text-amber-700 dark:text-amber-300 list-disc">
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Format a field name for display
 */
function formatFieldName(field: string): string {
  const fieldLabels: Record<string, string> = {
    name: 'Case Name',
    manufacturer: 'Manufacturer',
    sizeCategory: 'Size Category',
    'dimensions.height': 'Height',
    'dimensions.width': 'Width',
    'dimensions.depth': 'Depth',
    maxGpuLength: 'Max GPU Length',
    maxCpuCoolerHeight: 'Max CPU Cooler Height',
    maxPsuLength: 'Max PSU Length',
    releaseYear: 'Release Year',
    formFactorSupport: 'Form Factor Support',
    sidePanel: 'Side Panel',
  };

  return fieldLabels[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}
